import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card, Title, Text, Button, TextInput,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@tremor/react';
import AccessDenied from '../components/AccessDenied';
import MinMaxParamsForm from '../components/MinMaxParamsForm';
import {
  queueMinMaxJob, fetchMinMaxJob, defaultMinMaxParams, validateMinMaxParams, parseLowestMin,
} from '../api/minmax';
import type {
  MinMaxParams, MinMaxJobSnapshot, MinMaxProposal, MinMaxApplyRow, User,
} from '../api/types';
import { t } from '../lib/i18n';
import { canMinMax } from '../lib/roles';

/**
 * JCD-56 Phase 3 — batch min/max recalculation.
 *
 * Two steps on one page, mirroring minmaxup.p's own shape: choose the
 * parameters, then decide what to commit. The preview explains itself rather
 * than presenting a bare list — how many parts were reviewed, how many
 * proposals came out, and why the rest were skipped.
 */
type Edited = Record<string, { min: string; max: string; checked: boolean }>;

// A NUL separator, not a space: a part number may contain spaces, so
// ("A", "B C") would otherwise collide with ("A B", "C") and cross-wire two
// different rows edits.
const key = (p: MinMaxProposal) => `${p.sid}\u0000${p.line}\u0000${p.part}`;

export default function InventoryMinMax() {
  // InventoryLayout has already resolved and gated the user; it hands it
  // down through the outlet rather than making every tab re-fetch it.
  const user = useOutletContext<User>();
  const [params, setParams] = useState<MinMaxParams>(defaultMinMaxParams());
  const [lowestMinText, setLowestMinText] = useState('');
  const [sid, setSid] = useState('1');
  const [lines, setLines] = useState('');
  const [job, setJob] = useState<MinMaxJobSnapshot | null>(null);
  const [edited, setEdited] = useState<Edited>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (pollRef.current) window.clearTimeout(pollRef.current);
  }, []);

  const canRun = canMinMax(user);

  const proposals = job?.proposals ?? [];

  const selectedRows: MinMaxApplyRow[] = useMemo(
    () =>
      proposals
        .map((p) => {
          const e = edited[key(p)];
          const min = e ? Number(e.min) : p.new_min;
          const max = e ? Number(e.max) : p.new_max;
          return {
            sid: p.sid, line: p.line, part: p.part, min, max,
            overridden: min !== p.new_min || max !== p.new_max,
            _checked: e ? e.checked : true,
          };
        })
        .filter((r) => r._checked)
        .map(({ _checked, ...r }) => r),
    [proposals, edited],
  );

  async function poll(id: string) {
    try {
      const snap = await fetchMinMaxJob(id);
      setJob(snap);
      if (snap.state === 'queued' || snap.state === 'running') {
        pollRef.current = window.setTimeout(() => poll(id), 700);
        return;
      }
      setBusy(false);
      if (snap.state === 'failed') setErr(snap.error || 'job failed');
    } catch (e) {
      setBusy(false);
      setErr((e as Error).message);
    }
  }

  async function onCalc() {
    setErr(null);
    setApplyMsg(null);
    setJob(null);
    setEdited({});

    if (parseLowestMin(lowestMinText) === null) {
      setErr(t.part.minmax.err.lowestMin);
      return;
    }
    const bad = validateMinMaxParams(params);
    if (bad) {
      setErr((t.part.minmax.err as Record<string, string>)[bad] ?? bad);
      return;
    }

    setBusy(true);
    try {
      const id = await queueMinMaxJob({
        kind: 'preview',
        scope: { sid: Number(sid), lines, skip_no_history: true, skip_manual: true },
        params,
        stores: [Number(sid)],
      });
      poll(id);
    } catch (e) {
      setBusy(false);
      setErr((e as Error).message);
    }
  }

  async function onApply() {
    setErr(null);
    setApplyMsg(null);
    if (selectedRows.length === 0) return;
    setBusy(true);
    try {
      const id = await queueMinMaxJob({
        kind: 'apply', scope: { sid: Number(sid) }, params, rows: selectedRows,
      });
      const snap = await waitFor(id);
      const failed = (snap.applied ?? []).filter((r) => r.status !== 'ok' && r.status !== 'skipped');
      setApplyMsg(
        failed.length === 0
          ? `${t.minmaxPage.applied}: ${(snap.applied ?? []).filter((r) => r.status === 'ok').length}`
          : `${t.minmaxPage.appliedWithProblems}: ${failed.map((r) => `${r.line}/${r.part} (${r.status})`).join(', ')}`,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function waitFor(id: string): Promise<MinMaxJobSnapshot> {
    for (;;) {
      const snap = await fetchMinMaxJob(id);
      if (snap.state !== 'queued' && snap.state !== 'running') {
        if (snap.state === 'failed') throw new Error(snap.error || 'job failed');
        return snap;
      }
      await new Promise((r) => setTimeout(r, 500));
    }
  }

  function setRow(p: MinMaxProposal, patch: Partial<{ min: string; max: string; checked: boolean }>) {
    const k = key(p);
    setEdited((prev) => ({
      ...prev,
      [k]: {
        min: prev[k]?.min ?? String(p.new_min),
        max: prev[k]?.max ?? String(p.new_max),
        checked: prev[k]?.checked ?? true,
        ...patch,
      },
    }));
  }

  function setAll(checked: boolean) {
    const next: Edited = {};
    for (const p of proposals) {
      const k = key(p);
      next[k] = {
        min: edited[k]?.min ?? String(p.new_min),
        max: edited[k]?.max ?? String(p.new_max),
        checked,
      };
    }
    setEdited(next);
  }

  // The tab is hidden without the permission, but a direct URL still lands
  // here, so the page re-checks rather than trusting the strip.
  if (!canRun) return <AccessDenied user={user} />;

  const sk = job?.skipped;
  const skipParts = sk
    ? [
        sk.no_history ? `${t.minmaxPage.skip.noHistory} ${sk.no_history}` : '',
        sk.manual ? `${t.minmaxPage.skip.manual} ${sk.manual}` : '',
        sk.edate ? `${t.minmaxPage.skip.edate} ${sk.edate}` : '',
        sk.stock ? `${t.minmaxPage.skip.stock} ${sk.stock}` : '',
        sk.filters ? `${t.minmaxPage.skip.filters} ${sk.filters}` : '',
      ].filter(Boolean)
    : [];

  return (
    <div className="space-y-4">
      <Card>
        <Title>{t.minmaxPage.title}</Title>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
          <div>
            <label htmlFor="mm-sid" className="block text-sm">{t.minmaxPage.store}</label>
            <TextInput id="mm-sid" value={sid} disabled={busy}
              onChange={(e) => setSid(e.target.value)} />
          </div>
          <div>
            <label htmlFor="mm-lines" className="block text-sm">{t.minmaxPage.lines}</label>
            <TextInput id="mm-lines" value={lines} disabled={busy}
              onChange={(e) => setLines(e.target.value)} />
          </div>
        </div>

        <MinMaxParamsForm
          value={params}
          onChange={setParams}
          lowestMinText={lowestMinText}
          onLowestMinTextChange={setLowestMinText}
          disabled={busy}
        />

        <Button className="mt-4" onClick={onCalc} disabled={busy}>
          {t.part.minmax.calc}
        </Button>
        {err && <Text color="red" className="mt-3">{err}</Text>}
        {applyMsg && <Text color="green" className="mt-3">{applyMsg}</Text>}
        {busy && job === null && <Text className="mt-3">{t.minmaxPage.running}</Text>}
      </Card>

      {job && (job.state === 'running' || job.state === 'queued') && (
        <Card><Text>{t.minmaxPage.running} ({job.scanned})</Text></Card>
      )}

      {job && job.state === 'done' && (
        <Card>
          <Title>{t.minmaxPage.results}</Title>
          <Text className="mt-1">
            {proposals.length} {t.minmaxPage.proposalsOf} {job.scanned} {t.minmaxPage.partsReviewed}
          </Text>
          {skipParts.length > 0 && (
            <Text className="text-xs">{t.minmaxPage.skipped}: {skipParts.join(' · ')}</Text>
          )}
          {job.capped && (
            <Text color="amber" className="mt-2">{t.minmaxPage.capped}</Text>
          )}

          {proposals.length === 0 ? (
            <Text className="mt-3">{t.minmaxPage.empty}</Text>
          ) : (
            <>
              <div className="mt-3 flex gap-2">
                <Button variant="secondary" onClick={() => setAll(true)} disabled={busy}>
                  {t.minmaxPage.selectAll}
                </Button>
                <Button variant="secondary" onClick={() => setAll(false)} disabled={busy}>
                  {t.minmaxPage.selectNone}
                </Button>
                <a
                  className="text-tremor-brand hover:underline self-center"
                  href={`/api/minmax/jobs/${job.id}/rows.csv`}
                >
                  {t.minmaxPage.exportCsv}
                </a>
              </div>

              <Table className="mt-2">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.minmaxPage.cols.store}</TableHeaderCell>
                    <TableHeaderCell>{t.minmaxPage.cols.line}</TableHeaderCell>
                    <TableHeaderCell>{t.minmaxPage.cols.part}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.minmaxPage.cols.history}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.minmaxPage.cols.curMin}</TableHeaderCell>
                    <TableHeaderCell>{t.minmaxPage.cols.newMin}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.minmaxPage.cols.curMax}</TableHeaderCell>
                    <TableHeaderCell>{t.minmaxPage.cols.newMax}</TableHeaderCell>
                    <TableHeaderCell>{t.minmaxPage.cols.include}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposals.map((p) => {
                    const k = key(p);
                    const e = edited[k];
                    return (
                      <TableRow key={k}>
                        <TableCell>{p.sid}</TableCell>
                        <TableCell>{p.line}</TableCell>
                        <TableCell>{p.part}</TableCell>
                        <TableCell className="text-right">{p.hist_qty}</TableCell>
                        <TableCell className="text-right">{p.cur_min}</TableCell>
                        <TableCell>
                          <TextInput
                            aria-label={`min ${p.line}/${p.part}`}
                            value={e?.min ?? String(p.new_min)}
                            disabled={busy}
                            onChange={(ev) => setRow(p, { min: ev.target.value })}
                          />
                        </TableCell>
                        <TableCell className="text-right">{p.cur_max}</TableCell>
                        <TableCell>
                          <TextInput
                            aria-label={`max ${p.line}/${p.part}`}
                            value={e?.max ?? String(p.new_max)}
                            disabled={busy}
                            onChange={(ev) => setRow(p, { max: ev.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label={`incluir ${p.line}/${p.part}`}
                            checked={e?.checked ?? true}
                            disabled={busy}
                            onChange={(ev) => setRow(p, { checked: ev.target.checked })}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Button
                className="mt-3"
                onClick={onApply}
                disabled={busy || selectedRows.length === 0}
              >
                {t.minmaxPage.applyN} {selectedRows.length}
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
